import React, { useState } from 'react';
import {
  FaRocket, FaSearchDollar, FaHandshake, FaChartLine,
  FaCheckCircle, FaChevronDown, FaBuilding, FaLightbulb,
  FaCogs, FaUsers
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
const PrivateEquity = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What is Private Equity (PE) and who can invest?",
      a: "Private Equity involves investing in shares of privately held companies that are not listed on public stock exchanges. Due to higher minimum investment thresholds and regulatory requirements, these investments are typically reserved for High Net Worth Individuals (HNIs), Ultra-HNIs, Family Offices, and Institutional Investors."
    },
    {
      q: "What is the typical lock-in period for Private Equity investments?",
      a: "Private Equity is a highly illiquid asset class designed for patient capital. Typical PE funds or direct investments have lock-in periods ranging from 5 to 10 years, allowing the underlying companies time to execute their growth strategies and reach a profitable exit event (like an IPO or acquisition)."
    },
    {
      q: "How does Lotoswealth source its Private Equity deals?",
      a: "We leverage our deep institutional network, partnering with top-tier venture capital firms, specialized PE fund managers, and investment banks to bring our clients curated, heavily vetted opportunities that are rarely available to the general public."
    },
    {
      q: "What are the risks associated with Private Equity?",
      a: "While PE offers the potential for outsized returns, it carries significant risks including illiquidity, capital loss, and long realization timelines. Our rigorous due diligence process is strictly designed to mitigate these risks by thoroughly auditing management teams, business models, and market potential before any capital is committed."
    }
  ];

  return (
    <main className="private-equity-page">
      <Helmet>
        {/* Basic SEO */}
        <title>Private Equity Investment Services in Hyderabad | Private Equity Funds | Lotoswealth</title>

        <meta
          name="description"
          content="Lotoswealth offers Private Equity Investment Services in Hyderabad, providing access to high-growth private companies, pre-IPO investments, venture capital opportunities, and alternative investment strategies designed to build long-term wealth for high-net-worth investors."
        />

        <meta
          name="keywords"
          content="Private Equity Hyderabad, Private Equity Investment Hyderabad, Private Equity Funds Hyderabad, Private Equity Investment Services Hyderabad, Venture Capital Hyderabad, Pre IPO Investment Hyderabad, Startup Investments Hyderabad, Alternative Investments Hyderabad, High Net Worth Investments Hyderabad, HNI Investment Hyderabad, Portfolio Diversification Hyderabad, Wealth Management Hyderabad, Investment Advisor Hyderabad, Financial Advisor Hyderabad, Asset Management Hyderabad, Long Term Wealth Creation Hyderabad, Institutional Investments Hyderabad, Equity Investments Hyderabad, Investment Planning Hyderabad, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link rel="canonical" href="https://www.lotoswealth.com/private-equity" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta property="og:url" content="https://www.lotoswealth.com/private-equity" />
        <meta
          property="og:title"
          content="Private Equity Investment Services in Hyderabad | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Invest in high-growth private companies with Lotoswealth's Private Equity Investment Services in Hyderabad. Explore private equity funds, pre-IPO opportunities, venture capital, and alternative investments."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Private Equity Investment Services in Hyderabad | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Access exclusive private equity investment opportunities in Hyderabad. Grow your wealth through private equity funds, pre-IPO investments, venture capital, and alternative investment strategies with Lotoswealth."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>
      {/* =========================================
          1. HERO SECTION (With Ticker & Slow Zoom)
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '100vh', minHeight: '800px', overflow: 'hidden' }}>

        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 animate-slow-zoom">
          <img
            src="/images/private-equity.jpg"
            alt="Private Equity Advisory"
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text (Perfectly Centered) --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Private Equity Advisory
          </h1>
          <h2 className="animate-slide-up delay-2 fw-bold text-white mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Exclusive Capital for High-Growth Opportunities
          </h2>
          <p className="hero-description animate-slide-up delay-3 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Welcome to Lotoswealth. Some of the greatest wealth-generation opportunities exist outside of the public markets. Our Private Equity advisory provides sophisticated investors with access to the exclusive world of private markets, venture capital, and pre-IPO investments.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-4 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            By investing in unlisted companies, you gain the opportunity to participate in the value creation of disruptive startups, robust mid-market enterprises, and industry leaders long before they are available to the general public.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-5 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            We bring institutional-grade due diligence, rigorous risk assessment, and strategic foresight to every deal, ensuring your capital is deployed only in the most promising, high-conviction opportunities.
          </p>
        </div>

        {/* --- Scrolling Stock Ticker --- */}
        <div className="stock-ticker position-absolute bottom-0 w-100 z-3 bg-dark text-white border-top border-bottom border-secondary py-2" style={{ opacity: 0.9 }}>
          <div className="ticker-track">
            {/* First Set */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>

            {/* Duplicated Set for Infinite Scrolling */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
          </div>
        </div>
      </div>

      {/* =========================================
          2. WHY PRIVATE EQUITY MATTERS (Soft Teal Theme)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                Why Consider Private Equity?
              </h2>
              <p className="text-muted line-height-lg">
                The public markets represent only a fraction of the global economy. Many of today’s most innovative and rapidly scaling businesses are choosing to remain private for much longer, capturing their most significant growth phases away from public exchanges.
              </p>
              <p className="text-muted line-height-lg">
                Private Equity allows patient investors to partner directly with visionary founders and seasoned management teams. By accepting illiquidity, investors are historically rewarded with "illiquidity premiums"—returns that frequently outperform traditional public equity indices over the long term.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">The Private Market Advantage</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Outsized Returns:</strong> Target higher yields through early-stage growth.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Active Value Creation:</strong> PE managers actively improve company operations.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Low Market Volatility:</strong> Asset valuations are shielded from daily stock market panic.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Portfolio Diversification:</strong> True non-correlation to your public stock portfolio.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. OUR PE OFFERINGS (Crisp White)
      ========================================= */}
      <section className="py-5 bg-white" style={{ backgroundImage: "url('/images/lotoswealth-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>

        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">💼 Our Private Equity & Venture Solutions</h2>
            <p className="text-white">Curated access to premium private market opportunities.</p>
          </div>

          <div className="row g-4">

            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaRocket size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Venture Capital (VC)</h5>
                <p className="text-muted small">Invest in early-stage, disruptive tech startups and visionary founders poised to revolutionize industries. High risk, high reward capital allocation.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaChartLine size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Growth Equity</h5>
                <p className="text-muted small">Provide scaling capital to mature, proven businesses looking to expand operations, enter new markets, or make strategic acquisitions.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-3">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaHandshake size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Pre-IPO Placements</h5>
                <p className="text-muted small">Gain exclusive access to late-stage private companies preparing to go public, allowing you to secure equity before the IPO premium hits the market.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-4">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaBuilding size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Buyouts & Buy-ins</h5>
                <p className="text-muted small">Participate in funds that acquire controlling stakes in established companies to restructure, optimize operations, and unlock hidden value.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-5">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaUsers size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Co-Investments</h5>
                <p className="text-muted small">Invest directly alongside top-tier Private Equity fund managers in specific deals, often benefiting from reduced fee structures.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-6">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaCogs size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Distressed Assets & Turnaround</h5>
                <p className="text-muted small">Capitalize on fundamentally sound but financially distressed companies through specialized turnaround funds targeting significant undervaluation.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          4. OUR 5-STEP PE PROCESS (Dark Slate)
      ========================================= */}
      <section className="py-5 text-white" style={{ backgroundColor: '#0a192f' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔄 Our Due Diligence & Investment Process</h2>
            <p className="text-light opacity-75">Uncompromising standards for private market capital allocation.</p>
          </div>

          <div className="row g-4">

            <div className="col-md-6 col-lg-4 animate-slide-up delay-1">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 1 – Deal Origination</h5>
                <p className="small text-muted mb-0">We leverage our extensive institutional network to source exclusive, proprietary deals and top-quartile PE fund allocations.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-2">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 2 – Rigorous Due Diligence</h5>
                <p className="small text-muted mb-0">Intense scrutiny of the target’s financial health, management team, market size, regulatory environment, and competitive moats.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-3">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 3 – Valuation & Structuring</h5>
                <p className="small text-muted mb-0">Negotiating fair entry valuations, establishing protective shareholder rights, and structuring the deal for optimal tax efficiency.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-6 animate-slide-up delay-4">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 4 – Value Creation</h5>
                <p className="small text-muted mb-0">Partnering with PE fund managers who actively drive operational improvements, strategic pivots, and revenue growth in portfolio companies.</p>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 animate-slide-up delay-5">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 5 – Strategic Exit</h5>
                <p className="small text-muted mb-0">Monitoring the investment until a profitable liquidity event is achieved, whether through an IPO, strategic acquisition, or secondary market sale.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          5. WHO WE SERVE & WHY CHOOSE US (Soft Blue-Gray)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#eef2f5' }}>
        <div className="container py-4">
          <div className="row g-5">

            {/* Target Audience */}
            <div className="col-lg-5 animate-slide-up delay-1">
              <h3 className="fw-bold mb-4 text-dark">👥 Who Should Invest in PE?</h3>
              <p className="text-muted small mb-4">Private Equity is a specialized asset class strictly suited for:</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  "Ultra-High Net Worth Individuals (UHNIs)", "Family Offices",
                  "Institutional Investors", "Corporate Treasuries",
                  "Accredited Private Investors", "Investors with Patient Capital",
                  "Sophisticated Wealth Creators", "Long-Term Portfolio Diversifiers"
                ].map((item, idx) => (
                  <span key={idx} className="badge bg-white text-dark border p-2 shadow-sm fs-6">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Why Choose Lotoswealth */}
            <div className="col-lg-7 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <h3 className="fw-bold mb-3 text-dark">🏆 Why Lotoswealth for Private Markets?</h3>
                <p className="text-muted small mb-4">The private market lacks the transparency of public exchanges. We bridge that gap by offering:</p>
                <div className="row g-3">
                  {[
                    "Access to Top-Quartile Funds", "Exhaustive Background & Financial Vetting",
                    "Proprietary Deal Flow Sourcing", "Alignment of Interests & Co-investments",
                    "Transparent Quarterly Reporting", "Complex Structuring & Tax Advisory",
                    "Active Exit Strategy Planning", "Strict Confidentiality & Privacy"
                  ].map((item, idx) => (
                    <div key={idx} className="col-md-6 d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2 flex-shrink-0" />
                      <span className="small text-muted fw-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          6. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding Private Equity & Venture Capital</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          7. FINAL CTA SECTION (Deep Navy)
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Unlock the Power of Private Markets</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            Elevate your portfolio by accessing exclusive, high-growth private equity opportunities. Speak with our alternative investment specialists today.
          </p>
          <button className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3">
            Schedule a Private Equity Consultation
          </button>
        </div>
      </section>

    </main>
  );
};

export default PrivateEquity;